import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import moment from "moment";

// Contexts
import { AuthContext } from "../../context/AuthProvider";
import { ThemeContext } from "../../context/ThemeProvider";
import { BundleContext } from "../../context/BundleProvider";
import { useAnalytics } from "../../context/AnalyticsProvider";

// Components
import {
  ProfileHeader,
  PremiumBanner,
  PremiumDetails,
  ProfileModals,
  SupportEmailModal
} from "./components";
import { Section, SectionRow } from "../../components/Section";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRotate,
  faTrashCan,
  faUserXmark,
  faEnvelope,
  faRightFromBracket,
} from "@fortawesome/free-solid-svg-icons";

// API & Utils
import { auth } from "../../api/config";
import { setGhostUid, clearGhostUid, isGhostMode, getGhostEmail } from "../../api/ghostUid";
import {
  UPLOAD_PROFILE,
  DELETE_BUNDLE_USER,
  DELETE_USER,
  GET_STORE_AUTH_DETAILS
} from "../../api/firebase_user";
import { RESET_USER_QUESTIONS_COLLECTION } from "../../api/questions";
import { SHOW_TOAST } from "../../constant/utils";

// Constants
import { IS_BUNDLE_APP } from "../../../app_constant";

const APP_VERSION = "0.1.0";
let _profileTracked = false;

const Profile = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const { selectedCertificate } = useContext(BundleContext);
  const {
    bundleData,
    profile,
    setProfilePicture,
    setBundleProfilePicture,
    setNewProfile,
    setBundleProfile,
    setProfileDetails,
    isSubscribe,
  } = useContext(AuthContext);
  const { track, trackUserAction } = useAnalytics();

  // State management
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tempImage, setTempImage] = useState("");
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [customerPortalLink, setCustomerPortalLink] = useState(null);
  const [showGhostModal, setShowGhostModal] = useState(false);
  const [ghostUidInput, setGhostUidInput] = useState('');

  // Admin email — only this account can use ghost mode
  const ADMIN_EMAIL = 'smaddi@learnzapp.com';

  // Get user data
  const userData = {
    firstName: IS_BUNDLE_APP ? bundleData?.auth_first_name : profile?.auth_first_name,
    lastName: IS_BUNDLE_APP ? bundleData?.auth_last_name : profile?.auth_last_name,
    email: IS_BUNDLE_APP ? bundleData?.auth_email : profile?.auth_email,
    profilePic: tempImage || (IS_BUNDLE_APP ? bundleData?.profile_pic : profile?.profile_pic),
    subscription: IS_BUNDLE_APP ? bundleData?.subscription : profile?.subscription,
    examDate: profile?.onboarding_exam_date,
    loginType: getLoginMethod()
  };

  // Get login method
  function getLoginMethod() {
    const loginMethod = IS_BUNDLE_APP
      ? bundleData?.auth_login_provider
      : profile?.auth_login_provider;

    if (loginMethod == "google.com") {
      return "Sign in with Google";
    } else if (loginMethod == "apple.com") {
      return "Sign in with Apple";
    } else {
      return "Sign in with Email";
    }
  }

  // Get plan expiry
  function getPlanExpiry() {
    const profile_expiry = profile?.subscription?.subscription_expiration;
    const bundle_expiry = bundleData?.subscription?.subscription_expiration;

    const formatDateIfValid = (expiry) => {
      const expiryDate = moment(parseInt(expiry ?? "0"));
      return expiryDate.isAfter(moment()) ? expiryDate.format("DD MMM, YYYY hh:mm a") : null;
    };

    if (IS_BUNDLE_APP) {
      return bundle_expiry ? formatDateIfValid(bundle_expiry) :
             profile_expiry ? formatDateIfValid(profile_expiry) : null;
    } else {
      return profile_expiry ? formatDateIfValid(profile_expiry) : null;
    }
  }

  // Get plan expiry and premium status
  const expiryDate = getPlanExpiry();
  const isPremium = !!expiryDate;
  const isRecurring = userData.subscription?.paymentType === "recurring";

  // Track profile page view (once per page load)
  useEffect(() => {
    if (_profileTracked) return;
    _profileTracked = true;
    track('profile_viewed', {
      is_premium: isPremium,
      has_subscription: !!userData.subscription
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle sign out
  async function handleSignOut() {
    setIsLoading(true);

    trackUserAction('user_signed_out', {
      sign_out_source: 'profile_page'
    });

    await auth().signOut();
    setIsLoading(false);

    // Preserve theme preference
    const savedTheme = localStorage.getItem("Theme");
    localStorage.clear();
    if (savedTheme) localStorage.setItem("Theme", savedTheme);

    navigate("/");
  }

  // Handle delete account
  async function handleDeleteAccount() {
    setIsLoading(true);

    trackUserAction('account_deletion_attempted', {
      deletion_source: 'profile_page',
      had_subscription: !!userData.subscription
    });

    try {
    } catch (err) {
      console.error(err);
    }
    const uid = auth().currentUser?.uid;
    const provider = auth().currentUser?.providerData[0]?.providerId ?? "";
    const result = await DELETE_USER(uid);
    if (IS_BUNDLE_APP) {
      try {
        await DELETE_BUNDLE_USER(uid);
        localStorage.clear();
        navigate("/home");
      } catch (err) {
        console.error(err);
      }
    }
    try {
      const resultApps = await GET_STORE_AUTH_DETAILS();
      if (result.status == true) {
        setIsLoading(false);
        if (resultApps.status) {
          if (resultApps?.data?.apps?.length <= 1) {
            await auth().currentUser.delete();
          }
        }

        SHOW_TOAST("Your account has been deleted successfully", "success");
        localStorage.clear();
        navigate("/home");
      } else {
        setIsLoading(false);
        setTimeout(() => {
          SHOW_TOAST(result.error, "error");
        }, 1000);
      }
    } catch (e) {
      setIsLoading(false);
      setTimeout(() => {
        authError(e);
      }, 1000);
    }
    await handleSignOut();
  }

  // Handle auth error
  function authError(error) {
    if (error.code == "auth/requires-recent-login") {
      // Handle recent login requirement
    } else {
      // Handle other auth errors
    }
  }

  // Ghost mode: view app as another user (admin only)
  const versionClickCountRef = useRef(0);
  const versionClickTimerRef = useRef(null);

  function onVersionClick() {
    const currentEmail = auth().currentUser?.email?.toLowerCase();
    if (currentEmail !== ADMIN_EMAIL.toLowerCase()) return;

    versionClickCountRef.current += 1;
    if (versionClickTimerRef.current) clearTimeout(versionClickTimerRef.current);

    if (versionClickCountRef.current >= 5) {
      versionClickCountRef.current = 0;
      if (isGhostMode()) {
        exitGhostMode();
      } else {
        setGhostUidInput('');
        setShowGhostModal(true);
      }
    } else {
      versionClickTimerRef.current = setTimeout(() => {
        versionClickCountRef.current = 0;
      }, 2000);
    }
  }

  function enterGhostModeWithUid() {
    const uid = ghostUidInput.trim();
    if (!uid) {
      SHOW_TOAST('Please enter a user UID', 'error');
      return;
    }
    setGhostUid(uid, uid);
    setShowGhostModal(false);
    window.location.reload();
  }

  function exitGhostMode() {
    clearGhostUid();
    window.location.reload();
  }

  // Handle file upload
  const handleFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      fileInputRef.current.value = null;
      setTempImage(imageUrl);
      setShowImagePicker(false);

      setLoading(true);
      const result = await UPLOAD_PROFILE(file);
      setLoading(false);

      if (result.status) {
        if (IS_BUNDLE_APP) {
          setBundleProfilePicture(result.url);
        } else {
          setProfilePicture(result.url);
        }
      } else {
        SHOW_TOAST(result.error ?? "Failed to upload image");
      }
    }
  };

  // Handle reset app data
  async function handleResetData() {
    setLoading(true);
    await RESET_USER_QUESTIONS_COLLECTION();

    const resetData = {
      auth_first_name: profile?.auth_first_name ?? "",
      auth_last_name: profile?.auth_last_name ?? "",
      auth_email: profile?.auth_email ?? "",
      auth_login_provider: profile?.auth_login_provider ?? "",
      last_signin_time: IS_BUNDLE_APP
        ? profile?.last_signin_time ?? ""
        : auth().currentUser.metadata?.lastSignInTime ?? "",
      flashcard_bookmarks: [],
      flashcards_progress: [],
      practice_test_history: [],
      question_bookmarks: [],
      questions_progress: [],
      quiz_of_the_day_reminder: true,
      signup_date: IS_BUNDLE_APP
        ? profile?.signup_date ?? ""
        : auth().currentUser.metadata?.creationTime ?? "",
      study_plan_reminder: true,
      profile_pic: "",
      readiness_scores: null,
      score_progress: {},
      ...(profile?.isQuestionLatestVersionUpdated !== undefined && {
        isQuestionLatestVersionUpdated: profile.isQuestionLatestVersionUpdated
      }),
    };

    setNewProfile(resetData);

    if (IS_BUNDLE_APP) {
      const bundleResetData = {
        auth_first_name: bundleData?.auth_first_name ?? "",
        auth_last_name: bundleData?.auth_last_name ?? "",
        auth_email: bundleData?.auth_email ?? "",
        auth_login_provider: auth().currentUser?.providerData[0]?.providerId ?? "",
        last_signin_time: auth().currentUser.metadata?.lastSignInTime ?? "",
        signup_date: auth().currentUser.metadata?.creationTime ?? "",
        profile_pic: "",
      };
      setBundleProfile(bundleResetData);
    }

    setTimeout(() => {
      setLoading(false);
      navigate("/prepare", { state: { isFromOnboarding: true } });
    }, 1000);
  }

  // Handle customer portal
  const handleManageSubscription = async (event) => {
    event.preventDefault();
    setLoading(true);

    trackUserAction('subscription_management_accessed', {
      subscription_type: userData.subscription?.paymentType || 'unknown',
      is_recurring: isRecurring
    });

    try {
      const cachedPortalData = localStorage.getItem('stripePortalData');
      if (cachedPortalData) {
        const { url, timestamp } = JSON.parse(cachedPortalData);
        const oneHourAgo = Date.now() - (60 * 60 * 1000);

        if (timestamp > oneHourAgo) {
          setLoading(false);
          return url;
        }
      }

      const functionRef = firebase
        .app()
        .functions("us-central1")
        .httpsCallable("ext-firestore-stripe-payments-createPortalLink");

      const { data } = await functionRef({
        returnUrl: `${window.location.origin}/profile`,
        locale: "auto",
      });

      const portalData = {
        url: data.url,
        timestamp: Date.now()
      };
      localStorage.setItem('stripePortalData', JSON.stringify(portalData));

      setCustomerPortalLink(data.url);
      setLoading(false);

      return data.url;
    } catch (error) {
      console.error("Error fetching customer portal link:", error);
      setLoading(false);
      localStorage.removeItem('stripePortalData');

      let errorMessage = "Failed to open subscription management";
      if (error.code === 'functions/unavailable') {
        errorMessage = "Service temporarily unavailable. Please try again in a few minutes.";
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage = "Please sign in again to manage your subscription.";
      } else if (error.message?.includes('network')) {
        errorMessage = "Network error. Please check your connection and try again.";
      }

      SHOW_TOAST(errorMessage, "error");
      throw error;
    }
  };

  return (
    <div className="min-h-screen transition-colors duration-300"
         style={{ background: theme?.background?.main }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '3rem 1rem 2rem' }}>
        {/* Profile Header */}
        <ProfileHeader
          userData={userData}
          isPremium={isPremium}
          theme={theme}
          onImageClick={() => setShowImagePicker(true)}
          onEditPress={() => navigate('/account')}
        />

        {/* Premium Banner or Premium Details */}
        <div style={{ marginTop: '1.5rem' }}>
          {!isPremium ? (
            <PremiumBanner
              theme={theme}
              onViewPlans={() => navigate('/chooseplan')}
            />
          ) : (
            <PremiumDetails
              subscription={userData.subscription}
              theme={theme}
              onManageSubscription={handleManageSubscription}
              loading={loading}
            />
          )}
        </div>

        {/* ACCOUNT Section */}
        <div style={{ marginTop: '1.5rem' }}>
          <Section title="Account" theme={theme}>
            <SectionRow
              icon={faRotate}
              iconColor={theme?.primary?.blue?.[500] || '#3b82f6'}
              title="Recalculate Readiness Scores"
              subtitle="Recalculate all domain scores"
              onClick={() => {
                navigate("/prepare", { state: { isFromOnboarding: true } });
              }}
              theme={theme}
            />
            <SectionRow
              icon={faTrashCan}
              iconColor={theme?.primary?.orange?.[500] || '#f97316'}
              title="Reset All Data"
              subtitle="Clear all progress and start fresh"
              onClick={() => setShowResetModal(true)}
              theme={theme}
            />
            <SectionRow
              icon={faUserXmark}
              iconColor={theme?.primary?.red?.[500] || '#ef4444'}
              title="Delete Account"
              subtitle="Permanently delete your account and data"
              onClick={() => setShowDeleteModal(true)}
              theme={theme}
              isLast
            />
          </Section>

          {/* SUPPORT Section */}
          <Section title="Support" theme={theme}>
            <SectionRow
              icon={faEnvelope}
              iconColor={theme?.primary?.green?.[500] || '#22c55e'}
              title="Contact Support"
              subtitle="Get help with any issues"
              onClick={() => setShowSupportModal(true)}
              theme={theme}
              isLast
            />
          </Section>
        </div>

        {/* Sign Out Button */}
        <div
          onClick={() => setShowSignOutModal(true)}
          style={{
            textAlign: 'center',
            padding: '0.75rem',
            cursor: 'pointer',
            marginTop: '0.5rem',
          }}
        >
          <span style={{
            color: theme?.primary?.orange?.[500] || '#f97316',
            fontSize: '0.9375rem',
            fontWeight: '600',
          }}>
            <FontAwesomeIcon icon={faRightFromBracket} style={{ marginRight: '0.5rem' }} />
            Sign Out
          </span>
        </div>

        {/* Version Footer — 5 rapid clicks to activate ghost mode (admin only) */}
        <div
          onClick={onVersionClick}
          style={{
            textAlign: 'center',
            padding: '1rem 0 2rem',
            fontSize: '0.75rem',
            color: theme?.text?.tertiary || '#9ca3af',
            cursor: 'default',
            userSelect: 'none',
          }}
        >
          Version {APP_VERSION}
          {isGhostMode() && (
            <div style={{ color: '#FF6B35', fontWeight: '600', marginTop: '0.25rem' }}>
              Click 5x to exit ghost mode
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        accept="image/*"
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      {/* Modals */}
      <AnimatePresence>
        {showImagePicker && (
          <ProfileModals.ImagePickerModal
            isOpen={showImagePicker}
            onClose={() => setShowImagePicker(false)}
            onCamera={() => {
              setShowImagePicker(false);
            }}
            onGallery={() => {
              handleFileUpload();
            }}
            theme={theme}
          />
        )}

        {showResetModal && (
          <ProfileModals.ResetDataModal
            isOpen={showResetModal}
            onClose={() => setShowResetModal(false)}
            onConfirm={() => {
              setShowResetModal(false);
              handleResetData();
            }}
            theme={theme}
          />
        )}

        {showDeleteModal && (
          <ProfileModals.DeleteAccountModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={() => {
              setShowDeleteModal(false);
              handleDeleteAccount();
            }}
            theme={theme}
          />
        )}

        {showSignOutModal && (
          <ProfileModals.SignOutModal
            isOpen={showSignOutModal}
            onClose={() => setShowSignOutModal(false)}
            onConfirm={() => {
              setShowSignOutModal(false);
              handleSignOut();
            }}
            theme={theme}
          />
        )}

        {showSupportModal && (
          <SupportEmailModal
            isOpen={showSupportModal}
            onClose={() => setShowSupportModal(false)}
            theme={theme}
          />
        )}
      </AnimatePresence>

      {/* Ghost Mode Modal */}
      {showGhostModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => setShowGhostModal(false)}
        >
          <div
            style={{
              background: theme?.background?.card || '#fff',
              borderRadius: '1rem', padding: '2rem', width: '90%', maxWidth: '420px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', fontWeight: '700', color: theme?.text?.primary }}>
              Ghost Mode
            </h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: theme?.text?.secondary }}>
              Paste the user's Firebase UID to view the app as them
            </p>
            <input
              type="text"
              placeholder="e.g. pqTbgqBCrYNiXfSTNx51MQIPbFi2"
              value={ghostUidInput}
              onChange={(e) => setGhostUidInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && enterGhostModeWithUid()}
              style={{
                width: '100%', padding: '0.75rem', borderRadius: '0.5rem',
                border: `1px solid ${theme?.border?.light || '#d1d5db'}`,
                fontSize: '0.875rem', color: theme?.text?.primary,
                background: theme?.background?.main || '#f9fafb',
                boxSizing: 'border-box',
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                onClick={() => setShowGhostModal(false)}
                style={{
                  flex: 1, padding: '0.75rem', borderRadius: '0.5rem',
                  border: 'none', cursor: 'pointer', fontWeight: '600',
                  background: theme?.background?.secondary || '#f3f4f6',
                  color: theme?.text?.primary,
                }}
              >
                Cancel
              </button>
              <button
                onClick={enterGhostModeWithUid}
                style={{
                  flex: 1, padding: '0.75rem', borderRadius: '0.5rem',
                  border: 'none', cursor: 'pointer', fontWeight: '600',
                  background: '#3b82f6', color: '#fff',
                }}
              >
                Enter Ghost Mode
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
